"use client";

import React, { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { startOfWeek, endOfWeek, addMonths, subMonths } from "date-fns";
import { useUniversalAuth } from "@wtc/ui";
import type { CalendarEvent } from "@wtc/ui";

import { CalendarContext } from "./context";
import { computeCalendarSources } from "./sources";
import { createCalendarNavigationHelpers } from "./navigation";
import type {
  CalendarContextType,
  CalendarProviderProps,
  CalendarViewType,
} from "./types";

interface DisabledCombinedCalendarData {
  data: CalendarEvent[];
  isLoading: boolean;
  error: null;
  refetch: () => Promise<void>;
}

export function CalendarProvider({
  children,
  initialDate = new Date(),
  initialView = "dayGridMonth",
  dateRangeMonths = 4,
  // `enabled` and `dateRangeMonths` are retained for API stability — the
  // current hub build keeps live calendar fetching disabled (see below).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  enabled = true,
}: CalendarProviderProps): React.JSX.Element {
  const { appUser } = useUniversalAuth();
  const [currentDate, setCurrentDate] = React.useState<Date>(initialDate);
  const [currentView, setCurrentView] =
    React.useState<CalendarViewType>(initialView);
  const [selectedPerson, setSelectedPerson] = React.useState<string>("");
  const queryClient = useQueryClient();

  // Initialize selectedPerson with the signed-in user's email when user is available
  useEffect(() => {
    if (appUser?.email && !selectedPerson) {
      setSelectedPerson(appUser.email);
    }
  }, [appUser?.email, selectedPerson]);

  // Calculate extended date range for better caching
  // Preserved as-is for behavior parity with the previous implementation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const extendedStartDate = subMonths(currentDate, 1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const extendedEndDate = addMonths(currentDate, dateRangeMonths - 1);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 }); // Monday

  // Calendar functionality disabled in hub app to prevent 401 errors —
  // the hub app does not expose the required calendar API endpoints.
  const combined: DisabledCombinedCalendarData = {
    data: [],
    isLoading: false,
    error: null,
    refetch: () => Promise.resolve(),
  };

  const events = useMemo<CalendarEvent[]>(
    () => combined.data ?? [],
    [combined.data],
  );
  const isLoading = combined.isLoading;
  const error = combined.error ?? null;
  const refetch = combined.refetch;

  // Extract unique calendar sources and assign colors
  const { calendarSources, sourceColors } = useMemo(
    () => computeCalendarSources(events),
    [events],
  );

  const {
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    goToToday,
  } = createCalendarNavigationHelpers(setCurrentDate);

  // Cache invalidation
  const invalidateCache = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
    void queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
    void queryClient.invalidateQueries({ queryKey: ["microsoft-calendar"] });
  };

  const refetchEvents = (): void => {
    void refetch();
  };

  const contextValue: CalendarContextType = {
    // Data
    events,
    isLoading,
    error,

    // Source management
    calendarSources,
    sourceColors,

    // Date range management
    currentDate,
    setCurrentDate,
    weekStart,
    weekEnd,

    // View management
    currentView,
    setCurrentView,

    // Person selection
    selectedPerson,
    setSelectedPerson,

    // Utility functions
    refetchEvents,
    invalidateCache,

    // Navigation helpers
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    goToToday,
  };

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
}
